import theme from 'theme/index'
import {getSearchTerm} from 'helpers/string'
import {get, isEmpty, isNil} from 'lodash'
import {
  accountHasFeature,
  getProLicencesUsed,
  userHasFeature,
  userHasPermission,
} from 'helpers/user'
import {features, permissions, PRO_LICENCE} from 'helpers/constants'
import FinancialHelper from 'helpers/financial'
import {PartType} from 'helpers/parts'
import {isPrintTemplatePublished} from 'helpers/printTemplates'
import {isPurchaseOrderType, PrintTemplateType} from 'helpers/printTemplates/enum'

export const PurchaseOrderStatus = {
  draft: 0,
  issued: 1,
  partReceived: 2,
  received: 3,
  closed: 4,
  cancelled: 5,
  confirmed: 6,
  paid: 7,
}

export const isDraft = (status) => {
  return status === PurchaseOrderStatus.draft
}

export const isIssued = (status) => {
  return status === PurchaseOrderStatus.issued
}

export const PurchaseOrderActions = {
  draft: 'DRAFT',
  issue: 'ISSUE',
  close: 'CLOSE',
  cancel: 'CANCEL',
  receive: 'RECEIVE',
  markFullyReceived: 'MARK_AS_RECEIVED',
  return: 'RETURN',
  invoice: 'INVOICE',
  confirm: 'CONFIRM',
  unConfirm: 'UN_CONFIRM',
  markPaid: 'MARK_PAID',
  markFullyInvoiced: 'MARK_FULLY_INVOICED',
}

export const getPurchaseOrderStatusName = (purchaseOrderStatus) => {
  switch (purchaseOrderStatus) {
    case PurchaseOrderStatus.draft:
      return 'Draft'
    case PurchaseOrderStatus.issued:
      return 'Issued'
    case PurchaseOrderStatus.partReceived:
      return 'Part-Received'
    case PurchaseOrderStatus.received:
      return 'Received'
    case PurchaseOrderStatus.closed:
      return 'Closed'
    case PurchaseOrderStatus.cancelled:
      return 'Cancelled'
    case PurchaseOrderStatus.confirmed:
      return 'Confirmed'
    case PurchaseOrderStatus.paid:
      return 'Paid'
    default:
      return 'None'
  }
}

export const getPurchaseOrderActionVerb = (currentStatus, action) => {
  switch (currentStatus) {
    case PurchaseOrderStatus.draft:
      switch (action) {
        case PurchaseOrderActions.issue:
          return 'Issue'
        case PurchaseOrderActions.close:
          return 'Close'
        default:
          return 'None'
      }
    case PurchaseOrderStatus.issued:
      switch (action) {
        case PurchaseOrderActions.confirm:
          return 'Confirm'
        case PurchaseOrderActions.draft:
          return 'Un-Issue'
        case PurchaseOrderActions.receive:
          return 'Receive'
        case PurchaseOrderActions.cancel:
          return 'Cancel'
        case PurchaseOrderActions.invoice:
          return 'Invoice'
        case PurchaseOrderActions.markFullyInvoiced:
          return 'Mark Fully Invoiced'
        case PurchaseOrderActions.markPaid:
          return 'Mark as Paid'
        default:
          return 'None'
      }
    case PurchaseOrderStatus.confirmed:
      switch (action) {
        case PurchaseOrderActions.unConfirm:
          return 'Un-Confirm'
        case PurchaseOrderActions.receive:
          return 'Receive'
        case PurchaseOrderActions.cancel:
          return 'Cancel'
        case PurchaseOrderActions.invoice:
          return 'Invoice'
        case PurchaseOrderActions.markFullyInvoiced:
          return 'Mark Fully Invoiced'
        case PurchaseOrderActions.markPaid:
          return 'Mark as Paid'
        default:
          return 'None'
      }
    case PurchaseOrderStatus.partReceived:
      switch (action) {
        case PurchaseOrderActions.receive:
          return 'Receive'
        case PurchaseOrderActions.markFullyReceived:
          return 'Mark Fully Received'
        case PurchaseOrderActions.return:
          return 'Return'
        case PurchaseOrderActions.invoice:
          return 'Invoice'
        case PurchaseOrderActions.markFullyInvoiced:
          return 'Mark Fully Invoiced'
        case PurchaseOrderActions.markPaid:
          return 'Mark as Paid'
        default:
          return 'None'
      }
    case PurchaseOrderStatus.received:
      switch (action) {
        case PurchaseOrderActions.receive:
          return 'Receive'
        case PurchaseOrderActions.return:
          return 'Return'
        case PurchaseOrderActions.invoice:
          return 'Invoice'
        case PurchaseOrderActions.markFullyInvoiced:
          return 'Mark Fully Invoiced'
        case PurchaseOrderActions.markPaid:
          return 'Mark as Paid'
        default:
          return 'None'
      }
    case PurchaseOrderStatus.paid:
      switch (action) {
        case PurchaseOrderActions.receive:
          return 'Receive'
        case PurchaseOrderActions.return:
          return 'Return'
        default:
          return 'None'
      }
    case PurchaseOrderStatus.closed:
      switch (action) {
        case PurchaseOrderActions.draft:
          return 'Re-open'
        default:
          return 'None'
      }
    case PurchaseOrderStatus.cancelled:
      switch (action) {
        case PurchaseOrderActions.issue:
          return 'Re-open'
        default:
          return 'None'
      }

    default:
      return 'None'
  }
}

export const getColorForPurchaseOrderStatus = (purchaseOrderStatus) => {
  switch (purchaseOrderStatus) {
    case PurchaseOrderStatus.draft:
      return theme.palette.purchaseOrderStatus.draft
    case PurchaseOrderStatus.issued:
      return theme.palette.purchaseOrderStatus.issued
    case PurchaseOrderStatus.partReceived:
      return theme.palette.purchaseOrderStatus.partReceived
    case PurchaseOrderStatus.received:
      return theme.palette.purchaseOrderStatus.received
    case PurchaseOrderStatus.closed:
      return theme.palette.purchaseOrderStatus.closed
    case PurchaseOrderStatus.cancelled:
      return theme.palette.purchaseOrderStatus.cancelled
    case PurchaseOrderStatus.confirmed:
      return theme.palette.purchaseOrderStatus.confirmed
    case PurchaseOrderStatus.paid:
      return theme.palette.purchaseOrderStatus.paid
    default:
      return theme.palette.purchaseOrderStatus.draft
  }
}

export const getPurchaseOrderStatusOptions = () => {
  return [
    {
      value: PurchaseOrderStatus.draft,
      label: 'Draft',
      status: PurchaseOrderStatus.draft,
      invoiced: null,
    },
    {
      value: `${PurchaseOrderStatus.issued}:false`,
      invoiced: false,
      status: PurchaseOrderStatus.issued,
      label: 'Issued',
    },
    {
      value: `${PurchaseOrderStatus.issued}:true`,
      invoiced: true,
      status: PurchaseOrderStatus.issued,
      label: 'Issued & Invoiced',
    },
    {
      value: `${PurchaseOrderStatus.partReceived}:false`,
      invoiced: false,
      status: PurchaseOrderStatus.partReceived,
      label: 'Part-Received',
    },
    {
      value: `${PurchaseOrderStatus.partReceived}:true`,
      invoiced: true,
      status: PurchaseOrderStatus.partReceived,
      label: 'Part-Received & Invoiced',
    },
    {
      value: `${PurchaseOrderStatus.received}:false`,
      invoiced: false,
      status: PurchaseOrderStatus.received,
      label: 'Received',
    },
    {
      value: `${PurchaseOrderStatus.received}:true`,
      invoiced: true,
      status: PurchaseOrderStatus.received,
      label: 'Received & Invoiced',
    },
    {
      value: PurchaseOrderStatus.paid,
      invoiced: null,
      status: PurchaseOrderStatus.paid,
      label: 'Paid',
    },
    {
      value: PurchaseOrderStatus.closed,
      invoiced: null,
      status: PurchaseOrderStatus.closed,
      label: 'Closed',
    },
    {
      value: PurchaseOrderStatus.cancelled,
      invoiced: null,
      status: PurchaseOrderStatus.cancelled,
      label: 'Cancelled',
    },
    {
      value: `${PurchaseOrderStatus.confirmed}:false`,
      invoiced: false,
      status: PurchaseOrderStatus.confirmed,
      label: 'Confirmed',
    },
    {
      value: `${PurchaseOrderStatus.confirmed}:true`,
      invoiced: true,
      status: PurchaseOrderStatus.confirmed,
      label: 'Confirmed & Invoiced',
    },
  ]
}

export const getFilteredPurchaseOrderStatusOptions = (options) => {
  return options.filter(option => !option.invoiced)
}

export const getPurchaseOrderStatusOptionsFor = (purchaseOrder, disabledActions, purchaseOrderIsEmpty) => {
  const status = get(purchaseOrder, 'status')

  const getOptionFor = (action) => ({
    label: getPurchaseOrderActionVerb(status, action),
    id: action,
    key: action,
    value: action,
    disabled: disabledActions.includes(action),
  })

  const actions = []

  switch (status) {
    case PurchaseOrderStatus.draft:
      if (purchaseOrderIsEmpty) {
        actions.push(PurchaseOrderActions.close)
      } else actions.push(
          PurchaseOrderActions.issue,
          PurchaseOrderActions.close
      )
      break
    case PurchaseOrderStatus.issued:
      actions.push(
          PurchaseOrderActions.confirm,
          PurchaseOrderActions.receive,
          PurchaseOrderActions.invoice,
          !isPurchaseOrderInvoiced(purchaseOrder)
          && PurchaseOrderActions.markFullyInvoiced,
          !isPurchaseOrderInvoiced(purchaseOrder)
          && PurchaseOrderActions.draft,
          PurchaseOrderActions.cancel,
          isPurchaseOrderInvoiced(purchaseOrder)
          && PurchaseOrderActions.markPaid
      )
      break
    case PurchaseOrderStatus.confirmed:
      actions.push(
          PurchaseOrderActions.receive,
          PurchaseOrderActions.invoice,
          !isPurchaseOrderInvoiced(purchaseOrder)
          && PurchaseOrderActions.markFullyInvoiced,
          !isPurchaseOrderInvoiced(purchaseOrder)
          && PurchaseOrderActions.unConfirm,
          PurchaseOrderActions.cancel,
          isPurchaseOrderInvoiced(purchaseOrder)
          && PurchaseOrderActions.markPaid
      )
      break
    case PurchaseOrderStatus.partReceived:
      actions.push(
          PurchaseOrderActions.receive,
          PurchaseOrderActions.markFullyReceived,
          PurchaseOrderActions.invoice,
          !isPurchaseOrderInvoiced(purchaseOrder)
          && PurchaseOrderActions.markFullyInvoiced,
          PurchaseOrderActions.return,
          isPurchaseOrderInvoiced(purchaseOrder)
          && PurchaseOrderActions.markPaid
      )
      break
    case PurchaseOrderStatus.received:
      actions.push(
          PurchaseOrderActions.invoice,
          !isPurchaseOrderInvoiced(purchaseOrder)
          && PurchaseOrderActions.markFullyInvoiced,
          PurchaseOrderActions.return,
          isPurchaseOrderInvoiced(purchaseOrder)
          && PurchaseOrderActions.markPaid
      )
      break
    case PurchaseOrderStatus.paid:
      actions.push(PurchaseOrderActions.receive)
      if (isAnyPurchaseOrderItemReceived(purchaseOrder)) {
        actions.push(PurchaseOrderActions.return)
      }
      break
    case PurchaseOrderStatus.closed:
      actions.push(PurchaseOrderActions.draft)
      break
    case PurchaseOrderStatus.cancelled:
      actions.push(PurchaseOrderActions.issue)
      break
    default:
      break
  }

  return actions.filter(action => !isNil(action) && !!action).map(getOptionFor)
}

export const PurchaseOrderDateTypes = [
  {value: 'expected', label: 'Expected On'},
  {value: 'required', label: 'Required On'},
  {value: 'issued', label: 'Issued On'},
  {value: 'cancelled', label: 'Cancelled On'},
  {value: 'created', label: 'Created On'},
  {value: 'modified', label: 'Modified On'},
]

export const getPurchaseOrdersFilterParams = (
    searchTerm,
    status,
    companies,
    raisedBy,
    printedEmailed,
    dateTypes,
    targetStart,
    targetEnd) => {

  const params = {}

  if (getSearchTerm(searchTerm))
    params['searchTerm'] = searchTerm

  if (!isNil(status))
    params['status'] = status

  if (!isEmpty(companies))
    params['companyId'] = companies.map(option => option.value)

  if (!isEmpty(raisedBy))
    params['creatorId'] = raisedBy.map(option => option.value)

  if (!isEmpty(printedEmailed))
    params['printedEmailed'] = printedEmailed

  if (targetStart && targetEnd) {
    dateTypes.forEach(dateType => {
      params[`${dateType}Start`] = targetStart
      params[`${dateType}End`] = targetEnd
    })
  }

  params['total'] = true

  return params
}

export const getCalculatedTotals = (purchaseOrder, items, user) => {
  let totalCost = 0
  let totalTax = 0
  let subTotalCost = 0
  let subTotalTax = 0

  items.forEach(item => {
    const cost = FinancialHelper.round(item.netCost, 2)
    let discountedCost = cost

    if (purchaseOrder.discountPc) {
      discountedCost = FinancialHelper.round(cost, 10) - (FinancialHelper.round(cost, 10) * purchaseOrder.discountPc / 100)
      discountedCost = FinancialHelper.round(discountedCost, 2)
    }

    let tax = FinancialHelper.round(cost * item.taxRate, 10)
    let discountedTax = FinancialHelper.round(discountedCost * item.taxRate, 10)

    if (user.settings.lineItemTaxRounding) {
      tax = FinancialHelper.round(tax, 2)
      discountedTax = FinancialHelper.round(discountedTax, 2)
    }

    subTotalCost += cost
    subTotalTax += tax
    totalCost += discountedCost
    totalTax += discountedTax
  })

  return {
    ...purchaseOrder,
    subTotalCost,
    totalCost,
    subTotalTax,
    totalTax,
    totalCostIncTax: totalCost + totalTax,
  }
}

export const canEditItem = (item, user) => {
  if (!item) return false

  return isNil(item.partId)
      ? userHasPermission(user, permissions.ENTER_ITEM)
      : accountHasFeature(user, features.PRICE_LISTS_LICENCE)
}

export const canEditPartType = (part) => isNil(part)

// List of features
export const PurchaseOrderFeature = {
  EDIT_DESCRIPTION: 0,
  EDIT_CONTACT: 1,
  EDIT_DISCOUNT: 2,
  EDIT_TAX_CODE: 3,
  EDIT_LETTER_HEADER: 4,
  EDIT_LETTER_FOOTER: 5,
  EDIT_TERMS: 6,
  EDIT_EXPECTED_DATE: 7,
  EDIT_REQUIRED_DATE: 8,
  EDIT_ADDRESSES: 9,
  EDIT_NOTES: 10,
  EDIT_USER_FIELDS: 11,
  ADD_LINE_ITEMS: 12,
  EDIT_LINE_ITEMS: 13,
  DELETE_LINE_ITEMS: 14,
  REORDER_LINE_ITEMS: 15,
  CHANGE_STATUS: 16,
}

// List of features enabled at each stage
export const PurchaseOrderFeatures = {
  [PurchaseOrderStatus.draft]: {
    [PurchaseOrderFeature.EDIT_DESCRIPTION]: true,
    [PurchaseOrderFeature.EDIT_CONTACT]: true,
    [PurchaseOrderFeature.EDIT_DISCOUNT]: true,
    [PurchaseOrderFeature.EDIT_TAX_CODE]: true,
    [PurchaseOrderFeature.EDIT_LETTER_HEADER]: true,
    [PurchaseOrderFeature.EDIT_LETTER_FOOTER]: true,
    [PurchaseOrderFeature.EDIT_TERMS]: true,
    [PurchaseOrderFeature.EDIT_EXPECTED_DATE]: true,
    [PurchaseOrderFeature.EDIT_REQUIRED_DATE]: true,
    [PurchaseOrderFeature.EDIT_ADDRESSES]: true,
    [PurchaseOrderFeature.EDIT_NOTES]: true,
    [PurchaseOrderFeature.EDIT_USER_FIELDS]: true,
    [PurchaseOrderFeature.ADD_LINE_ITEMS]: true,
    [PurchaseOrderFeature.EDIT_LINE_ITEMS]: true,
    [PurchaseOrderFeature.DELETE_LINE_ITEMS]: true,
    [PurchaseOrderFeature.REORDER_LINE_ITEMS]: true,
    [PurchaseOrderFeature.CHANGE_STATUS]: true,
  },
  [PurchaseOrderStatus.issued]: {
    [PurchaseOrderFeature.EDIT_DESCRIPTION]: false,
    [PurchaseOrderFeature.EDIT_CONTACT]: true,
    [PurchaseOrderFeature.EDIT_DISCOUNT]: false,
    [PurchaseOrderFeature.EDIT_TAX_CODE]: false,
    [PurchaseOrderFeature.EDIT_LETTER_HEADER]: false,
    [PurchaseOrderFeature.EDIT_LETTER_FOOTER]: false,
    [PurchaseOrderFeature.EDIT_EXPECTED_DATE]: true,
    [PurchaseOrderFeature.EDIT_REQUIRED_DATE]: true,
    [PurchaseOrderFeature.EDIT_ADDRESSES]: true,
    [PurchaseOrderFeature.EDIT_NOTES]: true,
    [PurchaseOrderFeature.EDIT_USER_FIELDS]: true,
    [PurchaseOrderFeature.ADD_LINE_ITEMS]: false,
    [PurchaseOrderFeature.EDIT_LINE_ITEMS]: false,
    [PurchaseOrderFeature.DELETE_LINE_ITEMS]: false,
    [PurchaseOrderFeature.REORDER_LINE_ITEMS]: false,
    [PurchaseOrderFeature.CHANGE_STATUS]: true,
  },
  [PurchaseOrderStatus.confirmed]: {
    [PurchaseOrderFeature.EDIT_DESCRIPTION]: false,
    [PurchaseOrderFeature.EDIT_CONTACT]: false,
    [PurchaseOrderFeature.EDIT_DISCOUNT]: false,
    [PurchaseOrderFeature.EDIT_TAX_CODE]: false,
    [PurchaseOrderFeature.EDIT_LETTER_HEADER]: false,
    [PurchaseOrderFeature.EDIT_LETTER_FOOTER]: false,
    [PurchaseOrderFeature.EDIT_EXPECTED_DATE]: false,
    [PurchaseOrderFeature.EDIT_REQUIRED_DATE]: false,
    [PurchaseOrderFeature.EDIT_ADDRESSES]: false,
    [PurchaseOrderFeature.EDIT_NOTES]: false,
    [PurchaseOrderFeature.EDIT_USER_FIELDS]: true,
    [PurchaseOrderFeature.ADD_LINE_ITEMS]: false,
    [PurchaseOrderFeature.EDIT_LINE_ITEMS]: false,
    [PurchaseOrderFeature.DELETE_LINE_ITEMS]: false,
    [PurchaseOrderFeature.REORDER_LINE_ITEMS]: false,
    [PurchaseOrderFeature.CHANGE_STATUS]: true,
  },
  [PurchaseOrderStatus.received]: {
    [PurchaseOrderFeature.EDIT_DESCRIPTION]: false,
    [PurchaseOrderFeature.EDIT_CONTACT]: false,
    [PurchaseOrderFeature.EDIT_DISCOUNT]: false,
    [PurchaseOrderFeature.EDIT_TAX_CODE]: false,
    [PurchaseOrderFeature.EDIT_LETTER_HEADER]: false,
    [PurchaseOrderFeature.EDIT_LETTER_FOOTER]: false,
    [PurchaseOrderFeature.EDIT_EXPECTED_DATE]: false,
    [PurchaseOrderFeature.EDIT_REQUIRED_DATE]: false,
    [PurchaseOrderFeature.EDIT_ADDRESSES]: false,
    [PurchaseOrderFeature.EDIT_NOTES]: false,
    [PurchaseOrderFeature.EDIT_USER_FIELDS]: true,
    [PurchaseOrderFeature.ADD_LINE_ITEMS]: false,
    [PurchaseOrderFeature.EDIT_LINE_ITEMS]: false,
    [PurchaseOrderFeature.DELETE_LINE_ITEMS]: false,
    [PurchaseOrderFeature.REORDER_LINE_ITEMS]: false,
    [PurchaseOrderFeature.CHANGE_STATUS]: true,
  },
  [PurchaseOrderStatus.partReceived]: {
    [PurchaseOrderFeature.EDIT_DESCRIPTION]: false,
    [PurchaseOrderFeature.EDIT_CONTACT]: false,
    [PurchaseOrderFeature.EDIT_DISCOUNT]: false,
    [PurchaseOrderFeature.EDIT_TAX_CODE]: false,
    [PurchaseOrderFeature.EDIT_LETTER_HEADER]: false,
    [PurchaseOrderFeature.EDIT_LETTER_FOOTER]: false,
    [PurchaseOrderFeature.EDIT_EXPECTED_DATE]: false,
    [PurchaseOrderFeature.EDIT_REQUIRED_DATE]: false,
    [PurchaseOrderFeature.EDIT_ADDRESSES]: false,
    [PurchaseOrderFeature.EDIT_NOTES]: false,
    [PurchaseOrderFeature.EDIT_USER_FIELDS]: true,
    [PurchaseOrderFeature.ADD_LINE_ITEMS]: false,
    [PurchaseOrderFeature.EDIT_LINE_ITEMS]: false,
    [PurchaseOrderFeature.DELETE_LINE_ITEMS]: false,
    [PurchaseOrderFeature.REORDER_LINE_ITEMS]: false,
    [PurchaseOrderFeature.CHANGE_STATUS]: true,
  },
  [PurchaseOrderStatus.closed]: {
    [PurchaseOrderFeature.EDIT_DESCRIPTION]: false,
    [PurchaseOrderFeature.EDIT_CONTACT]: false,
    [PurchaseOrderFeature.EDIT_DISCOUNT]: false,
    [PurchaseOrderFeature.EDIT_TAX_CODE]: false,
    [PurchaseOrderFeature.EDIT_LETTER_HEADER]: false,
    [PurchaseOrderFeature.EDIT_LETTER_FOOTER]: false,
    [PurchaseOrderFeature.EDIT_TERMS]: false,
    [PurchaseOrderFeature.EDIT_EXPECTED_DATE]: false,
    [PurchaseOrderFeature.EDIT_REQUIRED_DATE]: false,
    [PurchaseOrderFeature.EDIT_ADDRESSES]: false,
    [PurchaseOrderFeature.EDIT_NOTES]: false,
    [PurchaseOrderFeature.EDIT_USER_FIELDS]: true,
    [PurchaseOrderFeature.ADD_LINE_ITEMS]: false,
    [PurchaseOrderFeature.EDIT_LINE_ITEMS]: false,
    [PurchaseOrderFeature.DELETE_LINE_ITEMS]: false,
    [PurchaseOrderFeature.REORDER_LINE_ITEMS]: false,
    [PurchaseOrderFeature.CHANGE_STATUS]: true,
  },
  [PurchaseOrderStatus.cancelled]: {
    [PurchaseOrderFeature.EDIT_DESCRIPTION]: false,
    [PurchaseOrderFeature.EDIT_CONTACT]: false,
    [PurchaseOrderFeature.EDIT_DISCOUNT]: false,
    [PurchaseOrderFeature.EDIT_TAX_CODE]: false,
    [PurchaseOrderFeature.EDIT_LETTER_HEADER]: false,
    [PurchaseOrderFeature.EDIT_LETTER_FOOTER]: false,
    [PurchaseOrderFeature.EDIT_TERMS]: false,
    [PurchaseOrderFeature.EDIT_EXPECTED_DATE]: false,
    [PurchaseOrderFeature.EDIT_REQUIRED_DATE]: false,
    [PurchaseOrderFeature.EDIT_ADDRESSES]: false,
    [PurchaseOrderFeature.EDIT_NOTES]: false,
    [PurchaseOrderFeature.EDIT_USER_FIELDS]: true,
    [PurchaseOrderFeature.ADD_LINE_ITEMS]: false,
    [PurchaseOrderFeature.EDIT_LINE_ITEMS]: false,
    [PurchaseOrderFeature.DELETE_LINE_ITEMS]: false,
    [PurchaseOrderFeature.REORDER_LINE_ITEMS]: false,
    [PurchaseOrderFeature.CHANGE_STATUS]: true,
  },
}

export const isFeatureEnabled = (feature, purchaseOrder) => {
  if (isNil(purchaseOrder))
    return false

  const features = PurchaseOrderFeatures[purchaseOrder.status]
  if (isEmpty(features)) return false
  const status = features[feature]
  return status !== null && status === true
}

export const getPurchasingFilterParams = (
    search,
    statusTypes,
    companies,
    contacts,
    dateTypes,
    targetStart,
    targetEnd) => {

  const params = {}

  if (getSearchTerm(search))
    params['search'] = search

  params['status'] = statusTypes

  if (!isEmpty(companies)) params['companyId'] = companies.map(company => company.value)
  if (!isEmpty(contacts)) params['creatorId'] = contacts

  if (targetStart && targetEnd) {
    dateTypes.forEach(dateType => {
      params[`${dateType}Start`] = targetStart
      params[`${dateType}End`] = targetEnd
    })
  }

  params['total'] = true
  return params
}

export const getDeliveryAddress = (purchaseOrder) => {
  return {
    deliveryOverridden: true,
    company: get(purchaseOrder, 'deliveryCompany', null),
    contact: get(purchaseOrder, 'deliveryContact', null),
    address1: get(purchaseOrder, 'deliveryAddress1', null),
    address2: get(purchaseOrder, 'deliveryAddress2', null),
    address3: get(purchaseOrder, 'deliveryAddress3', null),
    city: get(purchaseOrder, 'deliveryCity', null),
    county: get(purchaseOrder, 'deliveryCounty', null),
    postCode: get(purchaseOrder, 'deliveryPostCode', null),
    country: get(purchaseOrder, 'deliveryCountry', null),
    phone: get(purchaseOrder, 'deliveryPhone', null),
    mobile: get(purchaseOrder, 'deliveryMobile', null),
  }
}

export const updateDeliveryAddress = (purchaseOrder, address) => {
  const updatedPurchaseOrder = {...purchaseOrder}

  updatedPurchaseOrder.deliveryCompany = get(address, 'company', null)
  updatedPurchaseOrder.deliveryContact = get(address, 'contact', null)
  updatedPurchaseOrder.deliveryAddress1 = get(address, 'address1', null)
  updatedPurchaseOrder.deliveryAddress2 = get(address, 'address2', null)
  updatedPurchaseOrder.deliveryAddress3 = get(address, 'address3', null)
  updatedPurchaseOrder.deliveryCity = get(address, 'city', null)
  updatedPurchaseOrder.deliveryCounty = get(address, 'county', null)
  updatedPurchaseOrder.deliveryPostCode = get(address, 'postCode', null)
  updatedPurchaseOrder.deliveryCountry = get(address, 'country', null)
  updatedPurchaseOrder.deliveryPhone = get(address, 'phone', null)
  updatedPurchaseOrder.deliveryMobile = get(address, 'mobile', null)

  return updatedPurchaseOrder
}

export const canAddPartToPurchaseOrder = (part) => canAddPartTypeToPurchaseOrder(part.type)

export const canAddOrderItemToPurchaseOrder = (orderItem) => canAddPartTypeToPurchaseOrder(orderItem.partType)

export const canAddPartTypeToPurchaseOrder = (partType) => {
  switch (partType) {
    case PartType.MATERIAL.id:
    case PartType.OUTSOURCED.id:
    case PartType.NON_STOCK.id:
      return true
    default:
      return false
  }
}

export const isPurchasingEnabledForUser = (user) =>
    userHasFeature(user, features.PURCHASING_LICENCE) &&
    !isProPurchasingEnabled(user)

export const isProPurchasingEnabled = (user) => {
  const purchaseLicences = getProLicencesUsed(user, PRO_LICENCE.Purchase_Orders)
  return !isNil(purchaseLicences) && purchaseLicences > 0
}

export const getPurchaseOrderOption = (purchaseOrder) => {
  const status = purchaseOrder ? getPurchaseOrderStatusName(purchaseOrder.status) : ''
  return isNil(purchaseOrder)
      ? null
      : {
        value: purchaseOrder.id,
        label: `${purchaseOrder.reference}: ${purchaseOrder.description}`,
        purchaseOrder,
        status,
        className: 'purchaseOrder-' + status.toLowerCase(),
      }
}

export const isSignConfirmationDisabled = (acceptedDate, isItemsEmpty, status) => {
  return !isNil(acceptedDate) || isItemsEmpty && (!isDraft(status) || !isIssued(status))
}

export const getInferredLabel = (purchaseOrder) => {
  const invoicedStatuses = [
    PurchaseOrderStatus.issued,
    PurchaseOrderStatus.partReceived,
    PurchaseOrderStatus.received,
    PurchaseOrderStatus.confirmed,
  ]

  if (invoicedStatuses.includes(purchaseOrder.status) && purchaseOrder.invoiced) {
    return `${getPurchaseOrderStatusName(purchaseOrder.status)} & Invoiced`
  }

  return getPurchaseOrderStatusName(purchaseOrder.status)
}

export const getPrintTemplatesForPurchaseOrder = (printTemplates, purchaseOrder) => {
  if (isNil(printTemplates)) {
    throw new Error('Invalid argument')
  } else {

    const printTemplateTypes = []

    const status = get(purchaseOrder, 'status', null)
    switch (status) {
      case PurchaseOrderStatus.draft:
        printTemplateTypes.push(PrintTemplateType.DRAFT_PURCHASE_ORDER)
        printTemplateTypes.push(PrintTemplateType.PURCHASE_ORDER)
        break
      case PurchaseOrderStatus.issued:
        printTemplateTypes.push(PrintTemplateType.PURCHASE_ORDER)
        break
      case PurchaseOrderStatus.partReceived:
        printTemplateTypes.push(PrintTemplateType.PURCHASE_ORDER)
        break
      case PurchaseOrderStatus.received:
        printTemplateTypes.push(PrintTemplateType.PURCHASE_ORDER)
        break
      case PurchaseOrderStatus.closed:
        printTemplateTypes.push(PrintTemplateType.DRAFT_PURCHASE_ORDER)
        printTemplateTypes.push(PrintTemplateType.PURCHASE_ORDER)
        break
      case PurchaseOrderStatus.cancelled:
        printTemplateTypes.push(PrintTemplateType.DRAFT_PURCHASE_ORDER)
        printTemplateTypes.push(PrintTemplateType.PURCHASE_ORDER)
        break
      case PurchaseOrderStatus.confirmed:
        printTemplateTypes.push(PrintTemplateType.PURCHASE_ORDER)
        break
      default:
        break
    }

    const acceptedDate = get(purchaseOrder, 'acceptedDate', null)
    if (!isNil(acceptedDate))
      printTemplateTypes.push(PrintTemplateType.PURCHASE_ORDER_ACCEPTANCE)

    return printTemplates
        .filter(isPrintTemplatePublished)
        .filter(printTemplate => printTemplateTypes.includes(printTemplate.type))
  }
}

export const isPurchaseOrderHaveInvoicedValue = (purchaseOrder) => {
  const totalInvoiced = get(purchaseOrder, 'totalInvoiced', null)
  return totalInvoiced > 0
}

const nonMoveableStatuses = [
  PurchaseOrderStatus.partReceived,
  PurchaseOrderStatus.received,
]

export const canMovePurchaseOrder = (purchaseOrder) => {
  return !nonMoveableStatuses.includes(purchaseOrder.status)
}

export const isPurchaseOrderInvoiced = (purchaseOrder) => {
  return get(purchaseOrder, 'invoiced', false)
}

export const isAnyPurchaseOrderItemReceived = (purchaseOrder) => {
  const items = get(purchaseOrder, 'items', [])
  return items.some(item => item.quantityReceived > 0)
}

export const isDeliveryAddressPopulated = (purchaseOrder) => {
  const address1 = get(purchaseOrder, 'deliveryAddress1', null)
  const address2 = get(purchaseOrder, 'deliveryAddress2', null)
  const address3 = get(purchaseOrder, 'deliveryAddress3', null)
  const city = get(purchaseOrder, 'deliveryCity', null)
  const county = get(purchaseOrder, 'deliveryCounty', null)
  const postCode = get(purchaseOrder, 'deliveryPostCode', null)
  const country = get(purchaseOrder, 'deliveryCountry', null)

  return !(
      (isNil(address1) || isEmpty(address1))
      && (isNil(address2) || isEmpty(address2))
      && (isNil(address3) || isEmpty(address3))
      && (isNil(city) || isEmpty(city))
      && (isNil(county) || isEmpty(county))
      && (isNil(postCode) || isEmpty(postCode))
      && (isNil(country) || isEmpty(country))
  )
}

export const isAbleToInvoicePurchaseOrder = (purchaseOrder) => {
  return purchaseOrder == null ||
    !(purchaseOrder.status === PurchaseOrderStatus.draft
    || purchaseOrder.status === PurchaseOrderStatus.cancelled
    || purchaseOrder.status === PurchaseOrderStatus.closed)

}

export const isDraftPOAndPOTypeTemplate = (purchaseOrder, template) => {
  const status = get(purchaseOrder, 'status')
  return isDraft(status) && isPurchaseOrderType(template)
}

export const getRemainingToBeInvoiced = (purchaseOrder) => {
  return Math.max(0, purchaseOrder.totalCost - purchaseOrder.totalInvoiced)
}
